/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextField, Grid, Snackbar, Alert, ToggleButton, ToggleButtonGroup } from "@mui/material";

import { useAppContext } from "../core/AppContext";
import * as Yup from "yup";
import { initUserAccountService, PhoneUserAccount } from "../security/userAccountService";
import { initUserProfileService, UserProfile } from "../security/userProfileService";
import { useSiteService } from "../sites/siteService";
import { useTenantService } from "../tenants/tenantService";
import { AppName, AppRoleValues, BITMESH_TENANT } from "../core/AppConfig";
import { FormContainer, FormHeader, FormSection } from "../shared/FormLayout";
import { ProfilePicker } from "../shared/ProfilePicker";
import { ProfileAccessFields } from "../profiles/ProfileAccessFields";

type Props = {
  account: PhoneUserAccount | null;
};

const sortByName = (items: any[]) => items.sort((a: any, b: any) => a.name.localeCompare(b.name));

export const EditPhoneAccountForm: FC<Props> = ({ account }) => {
  const app = useAppContext();
  const accountService = initUserAccountService();
  const profileService = initUserProfileService();
  const siteService = useSiteService();
  const tenantService = useTenantService();

  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<string|null>(account?.userProfileId ?? null);
  const [savedProfileId, setSavedProfileId] = useState<string|null>(account?.userProfileId ?? null);
  const [profileMode, setProfileMode] = useState<"new" | "existing">("new");
  const [profileErrorMessage, setProfileErrorMessage] = useState<string>("");
  const [successOpen, setSuccessOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Profile fields (used when creating new account)
  const [tenants, setTenants] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [selectedSites, setSelectedSites] = useState<any[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<any[]>([]);
  const [fullAccessTenants, setFullAccessTenants] = useState<any[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<any[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  useEffect(() => {
    profileService.getUserProfiles().then(setProfiles);
    tenantService.getAll().then(tenants => setTenants(sortByName(tenants)));
    siteService.getAll().then(sites => {
      const sortedSites = sortByName(sites);
      setSites(sortedSites);
      setSiteOptions(sortedSites);
    });
  }, [app.user]);

  useEffect(() => {
    const needsExistingProfile = account || (!account && profileMode === "existing");
    if (needsExistingProfile && !selectedProfile) {
      setProfileErrorMessage("Velg en profil");
    } else {
      setProfileErrorMessage("");
    }
  }, [selectedProfile, profileMode]);

  useEffect(() => {
    if (selectedTenants.length === 0) {
      setSiteOptions(sites);
    } else {
      const filteredSites = sites.filter(site =>
        selectedTenants.includes(BITMESH_TENANT) || selectedTenants.includes(site.tenantId)
      );
      setSiteOptions(sortByName(filteredSites));
    }
  }, [selectedTenants, sites]);

  const createNewProfile = !account && profileMode === "new";
  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.string().required("Telefon er påkrevd"),
    ...(createNewProfile ? { name: Yup.string().required("Visningsnavn er påkrevd") } : {}),
  });
  const formOptions = {
    defaultValues: {
      phoneNumber: account?.phoneNumber ?? "",
      name: "",
    },
    resolver: yupResolver(validationSchema),
  };

  const { register, handleSubmit, formState } =
    useForm(formOptions);
  const { errors, isSubmitting } = formState;

  async function onSubmit({ phoneNumber, name }: any) {
    setErrorMessage(null);
    try {
      if (!account) {
        let profileId: string;
        if (profileMode === "new") {
          const newProfile = await profileService.addUserProfile({
            name,
            siteIds: selectedSites,
            tenantIds: selectedTenants,
            fullAccessTenantIds: fullAccessTenants,
            isPublic,
            permissions: selectedPermissions,
            roles: selectedApplications.map((appName: AppName) => AppRoleValues[appName]),
          });
          profileId = newProfile.id;
        } else {
          if (!selectedProfile) {
            setProfileErrorMessage("Velg en profil");
            return;
          }
          profileId = selectedProfile;
        }
        await accountService.addUserAccount({
          phoneNumber: phoneNumber as string,
          type: "PhoneUserAccount",
          externalUserId: phoneNumber,
          userProfileId: profileId,
        });
      } else {
        if (!selectedProfile) {
          setProfileErrorMessage("Velg en profil");
          return;
        }
        await accountService.updateUserAccount({
          id: account.id,
          phoneNumber: phoneNumber as string,
          type: "PhoneUserAccount",
          externalUserId: phoneNumber,
          userProfileId: selectedProfile,
        });
        setSavedProfileId(selectedProfile);
      }
      setSuccessOpen(true);
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message || "An unexpected error occurred";
      setErrorMessage(message);
    }
  }

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <FormHeader section="Telefonkontoer" title={account?.phoneNumber || "Ny telefonkonto"} isSubmitting={isSubmitting} />

      <FormSection title="Konto">
        <Grid item xs={12} md={6}>
          <TextField
            label="Telefon"
            size="small"
            fullWidth
            {...register("phoneNumber")}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber?.message}
          />
        </Grid>
        {!account && (
          <Grid item xs={12} md={6}>
            <ToggleButtonGroup
              value={profileMode}
              exclusive
              color="primary"
              onChange={(_, value) => { if (value) setProfileMode(value); }}
              size="small"
              aria-label="Profil"
            >
              <ToggleButton value="new">Ny profil</ToggleButton>
              <ToggleButton value="existing">Eksisterende profil</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        )}
      </FormSection>

      {createNewProfile ? (
        <>
          <FormSection title="Ny profil">
            <Grid item xs={12} md={6}>
              <TextField
                label="Visningsnavn"
                size="small"
                fullWidth
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            </Grid>
          </FormSection>
          <ProfileAccessFields
            tenants={tenants}
            sites={sites}
            siteOptions={siteOptions}
            selectedApplications={selectedApplications}
            setSelectedApplications={setSelectedApplications}
            fullAccessTenants={fullAccessTenants}
            setFullAccessTenants={setFullAccessTenants}
            selectedTenants={selectedTenants}
            setSelectedTenants={setSelectedTenants}
            selectedSites={selectedSites}
            setSelectedSites={setSelectedSites}
            isPublic={isPublic}
            setIsPublic={setIsPublic}
            selectedPermissions={selectedPermissions}
            setSelectedPermissions={setSelectedPermissions}
          />
        </>
      ) : (
        <FormSection title="Profil">
          <Grid item xs={12}>
            <ProfilePicker
              profiles={profiles}
              selected={selectedProfile}
              onSelect={setSelectedProfile}
              savedProfileId={account ? savedProfileId : undefined}
              error={profileErrorMessage}
            />
          </Grid>
        </FormSection>
      )}

      <Snackbar open={successOpen} autoHideDuration={3000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: '100%' }}>
          Telefonkontoen er {!account ? 'opprettet' : 'oppdatert'}.
        </Alert>
      </Snackbar>
      <Snackbar open={!!errorMessage} autoHideDuration={10000} onClose={() => setErrorMessage(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </FormContainer>
  );
};
